/** Voice activation sensitivity mode */
export type InputSensitivityMode = 'automatic' | 'manual'

/** Default RMS threshold for manual mode (0..1 range, logarithmic feel) */
export const DEFAULT_THRESHOLD = 0.0001

/** Minimum / maximum slider values (normalized 0-1) */
export const MIN_THRESHOLD = 0
export const MAX_THRESHOLD = 0.15

// ---------------------------------------------------------------
// Auto-threshold (adaptive noise floor) constants
// ---------------------------------------------------------------

/** How fast the noise floor estimate rises toward current RMS when gate is closed (avoids tracking speech). Tuned for AudioWorklet rate (~375 Hz at 48 kHz). */
export const NOISE_FLOOR_ATTACK = 0.00016

/**
 * Slow upward tracking rate used while the gate is open.
 * Handles constant noise that keeps the gate permanently open:
 * the floor gradually rises to match the noise, and the auto-threshold follows.
 * Tuned for AudioWorklet rate (~375 Hz at 48 kHz).
 */
export const NOISE_FLOOR_ATTACK_OPEN = 0.000064

/** How fast the noise floor estimate falls when RMS drops (very slow — holds during pauses). Tuned for AudioWorklet rate (~375 Hz at 48 kHz). */
export const NOISE_FLOOR_RELEASE = 0.000963

/**
 * Grace period (ms) after the gate closes before the noise floor resumes updating.
 * Prevents micro-pauses between words from ratcheting the floor upward
 * (speech tail / room reverb is still elevated right after the gate closes).
 */
export const NOISE_FLOOR_HOLD_MS = 300

/** Headroom multiplier above the noise floor for the auto-threshold. */
export const AUTO_THRESHOLD_MULTIPLIER = 2

/** Additive offset so the auto-threshold is never zero even in dead silence. */
export const AUTO_THRESHOLD_OFFSET = 0.0015

/** Absolute minimum auto-threshold (prevents gate from opening on near-zero noise). */
export const AUTO_THRESHOLD_MIN = 0

/** Absolute maximum auto-threshold (cap for very noisy environments). */
export const AUTO_THRESHOLD_MAX = 0.05

/** Hysteresis ratio: gate closes at effectiveThreshold * HYSTERESIS_RATIO. */
export const HYSTERESIS_RATIO = 0.6

/**
 * Maximum ratio above the current noise floor that RMS is allowed to
 * contribute during gate-open floor updates. Caps the EMA input so that
 * loud speech (e.g. phone mic with high gain) doesn't push the floor up
 * faster than moderate speech on a quieter device.
 */
export const NOISE_FLOOR_MAX_TRACK_RATIO = 5

/** Initial noise floor seed before any audio is analyzed. */
export const INITIAL_NOISE_FLOOR = 0.005
